import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import CommonChip from '../../common_components/CommonChip';
import HoverTooltip from '../../common_components/hoverOverlay/HoverTooltip';
import { getChannelIcon, formatCurrency, formatDate, TYPE_LABEL_MAP, formatChannelName } from './shared';
export const useGoalTableConfigs = ({ goal_type, prevYear, year_month, onActionClick }) => {
    const getMonthLabel = (ym, offset = 0) => {
        if (!ym)
            return '';
        const [y, m] = ym.split('-').map(Number);
        const d = new Date(y, m - 1 + offset, 1);
        return d.toLocaleString('en-us', { month: 'short' });
    };
    const renderMeasurementChannels = (channels = []) => {
        const sortedChannels = [...channels].sort((a, b) => {
            const iconA = getChannelIcon(a);
            const iconB = getChannelIcon(b);
            if (iconA && !iconB)
                return -1;
            if (!iconA && iconB)
                return 1;
            return 0;
        });
        const showMore = sortedChannels.length > 2;
        const visibleChannels = showMore ? sortedChannels.slice(0, 2) : sortedChannels;
        const remainingChannels = sortedChannels.slice(2);
        return (_jsx(Box, { sx: { display: 'flex', flexWrap: 'wrap', gap: 1, py: '2px', alignItems: 'center' }, children: visibleChannels.length > 0 ? (_jsxs(_Fragment, { children: [visibleChannels.map((m, idx) => {
                        const iconPath = getChannelIcon(m);
                        if (!iconPath) {
                            return (_jsx(CommonChip, { label: formatChannelName(m), index: 0, renderType: "tag", sx: { py: '2px', px: 1, fontSize: '11px', fontWeight: 500 } }, idx));
                        }
                        return (_jsx(HoverTooltip, { title: _jsx(Box, { children: formatChannelName(m) }), arrow: true, placement: "top", children: _jsx(Box, { sx: {
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s',
                                    '&:hover': {
                                        transform: 'scale(1.1)'
                                    }
                                }, children: _jsx("img", { src: iconPath, alt: m, style: { width: '18px', height: '18px', objectFit: 'contain' } }) }) }, idx));
                    }), showMore && (_jsx(HoverTooltip, { title: _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1, p: 0.5 }, children: remainingChannels.map((m, i) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [getChannelIcon(m) && (_jsx("img", { src: getChannelIcon(m), alt: "", style: { width: 14, height: 14 } })), _jsx(Typography, { sx: { fontSize: '12px' }, children: formatChannelName(m) })] }, i))) }), arrow: true, placement: "top", children: _jsxs(Typography, { sx: {
                                fontSize: '11px',
                                fontWeight: 600,
                                color: '#667085',
                                cursor: 'pointer',
                                ml: 0.5,
                                '&:hover': { textDecoration: 'underline' }
                            }, children: ["+", remainingChannels.length] }) }))] })) : '-' }));
    };
    const columns = useMemo(() => {
        if (goal_type === 'commission_goal') {
            return [
                { id: 'clientName', label: 'Client Name', minWidth: 180, sortable: true },
                { id: 'amName', label: 'AM', minWidth: 150 },
                { id: 'type', label: 'Type', minWidth: 130 },
                { id: 'startDate', label: 'Start Date', minWidth: 130, sortable: true },
                { id: 'endDate', label: 'End Date', minWidth: 130, sortable: true },
                { id: 'measurement', label: 'Measurement Channel', minWidth: 220 },
                { id: 'commissionRate', label: 'Commission Rate', minWidth: 140, sortable: true }
            ];
        }
        if (goal_type === 'am_projection') {
            return [
                { id: 'clientName', label: 'Client Name', minWidth: 180, sortable: true },
                { id: 'amName', label: 'AM', minWidth: 150, sortable: true },
                { id: 'type', label: 'Type', minWidth: 130 },
                { id: 'approval_status', label: 'Approval Status', minWidth: 150, align: 'left', sortable: true },
                { id: 'curr_plan', label: `${getMonthLabel(year_month)} Plan for Hitting Goals`, minWidth: 180, align: 'left', draggable: false },
                { id: 'prev1_plan', label: `${getMonthLabel(year_month, 1)} Plan for Hitting Goals`, minWidth: 180, align: 'left', draggable: false },
                { id: 'prev2_plan', label: `${getMonthLabel(year_month, 2)} Plan for Hitting Goals`, minWidth: 180, align: 'left', draggable: false },
                { id: 'note_action', label: 'Monthly Progress Notes', minWidth: 180, align: 'left', draggable: false }
            ];
        }
        return [
            { id: 'clientName', label: 'Client Name', minWidth: 180, sortable: true },
            { id: 'amName', label: 'AM', minWidth: 150 },
            { id: 'type', label: 'Type', minWidth: 130 },
            { id: 'prevYearRev', label: `Prev. Year Rev (${prevYear})`, minWidth: 150, align: 'left', sortable: true },
            { id: 'growthRate', label: 'YoY Growth Rate', minWidth: 140, align: 'left', sortable: true },
            { id: 'revenue', label: `Proj Rev (${year_month?.split('-')[0] || ''})`, minWidth: 160, align: 'left', sortable: true },
            { id: 'measurement', label: 'Measurement Channel', minWidth: 200 }
        ];
    }, [goal_type, prevYear]);
    const renderCell = (row, column) => {
        const { summary, tabs } = row;
        if (!summary)
            return '-';
        switch (column.id) {
            case 'clientName': {
                let types = [];
                if (goal_type === 'commission_goal') {
                    summary.type?.forEach((t) => {
                        const val = t === 'paid_media' ? tabs?.paid_comm_type : tabs?.aff_type;
                        if (val && val !== '-')
                            types.push(val);
                    });
                }
                else if (goal_type !== 'am_projection') {
                    summary.type?.forEach((t) => {
                        if (t === 'paid_media') {
                            const spendTypes = summary.paid_spend_type || [];
                            spendTypes.forEach((st) => {
                                if (st && st !== '-')
                                    types.push(st);
                            });
                        }
                        else if (t === 'affiliate') {
                            const affType = tabs?.aff_account_type;
                            if (affType && affType !== '-')
                                types.push(affType);
                        }
                    });
                }
                const uniqueTypes = Array.from(new Set(types));
                const getChipDetails = (str) => {
                    const lower = str.toLowerCase();
                    if (lower.includes('revenue'))
                        return { letter: 'R', label: 'Revenue' };
                    if (lower.includes('order'))
                        return { letter: 'O', label: 'Order' };
                    if (lower.includes('budget'))
                        return { letter: 'BB', label: 'Budget-Based' };
                    if (lower.includes('roas'))
                        return { letter: 'RB', label: 'ROAS-Based' };
                    return { letter: str.charAt(0).toUpperCase(), label: str };
                };
                return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Typography, { sx: { fontSize: '14px !important', fontWeight: 400, padding: 0 }, children: summary.clientName || '-' }), uniqueTypes.length > 0 && (_jsx(Box, { sx: { display: 'flex', position: 'relative', zIndex: 1 }, children: uniqueTypes.map((typeStr, idx) => {
                                const { letter, label } = getChipDetails(typeStr);
                                return (_jsx(CommonChip, { label: letter, tooltip: label, renderType: "circle", index: 4, className: `circle manager-circle ${idx === 0 ? "first" : ""}`, sx: { marginLeft: idx > 0 ? '-6px' : '0px' } }, idx));
                            }) }))] }));
            }
            case 'type':
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: summary.type?.map((t) => (_jsx(CommonChip, { label: TYPE_LABEL_MAP[t] || t, index: t === 'paid_media' ? 3 : 4, sx: { textTransform: 'capitalize', alignSelf: 'flex-start', py: '1px', fontSize: '14px', fontWeight: 500 } }, t))) }));
            case 'amName': {
                if (!summary.am || summary.am.length === 0)
                    return '-';
                const names = summary.am.map((a) => a.name).filter(Boolean);
                if (names.length === 0)
                    return '-';
                const visible = names.slice(0, 2);
                const extra = names.slice(2);
                const getInitials = (name) => name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase();
                return (_jsx(Box, { sx: { display: 'flex' }, children: _jsxs(Box, { sx: { display: 'flex', position: 'relative', zIndex: 1 }, children: [visible.map((name, idx) => (_jsx(CommonChip, { label: name === "NA" ? "NA" : getInitials(name), tooltip: name, renderType: "circle", index: idx, className: `circle manager-circle ${idx === 0 ? "first" : ""} ${name === "NA" ? "na" : "ok"}`, sx: name === "NA" ? { background: "#F2F4F7", color: "#344054" } : {} }, `${name}-${idx}`))), extra.length > 0 && (_jsx(CommonChip, { label: `+${extra.length}`, renderType: "circle", className: "circle extra-circle manager-circle", tooltip: _jsx(Box, { sx: { background: "#27272a", borderRadius: "12px", overflow: "hidden", minWidth: 180, color: 'white' }, children: extra.map((n, i) => (_jsx(Box, { sx: {
                                            px: 1, py: 1, fontSize: "14px", textTransform: "capitalize",
                                            borderBottom: i !== extra.length - 1 ? "1px solid rgba(255,255,255,0.2)" : "none"
                                        }, children: n }, `${n}-${i}`))) }) }))] }) }));
            }
            case 'startDate':
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: summary.type?.map((t) => {
                        const date = t === 'paid_media' ? summary.paid_start_date : summary.aff_start_date;
                        return (_jsx(Typography, { sx: { fontSize: '14px !important', py: '2px' }, children: formatDate(date) }, t));
                    }) }));
            case 'endDate':
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: summary.type?.map((t) => {
                        const date = t === 'paid_media' ? summary.paid_end_date : summary.aff_end_date;
                        return (_jsx(Typography, { sx: { fontSize: '14px !important', py: '2px' }, children: formatDate(date) }, t));
                    }) }));
            case 'commDaysInMonth':
                return _jsx(Typography, { sx: { fontSize: '14px !important' }, children: summary.comm_days_in_month ?? '-' });
            case 'commDaysBilledInMonth':
                return _jsx(Typography, { sx: { fontSize: '14px !important' }, children: summary.comm_days_billed_in_month ?? '-' });
            case 'mgmtDaysInMonth':
                return _jsx(Typography, { sx: { fontSize: '14px !important' }, children: summary.mgmt_days_in_month ?? '-' });
            case 'mgmtDaysBilledInMonth':
                return _jsx(Typography, { sx: { fontSize: '14px !important' }, children: summary.mgmt_days_billed_in_month ?? '-' });
            case 'fullMgmtFees':
                return _jsx(Typography, { sx: { fontSize: '14px !important' }, children: formatCurrency(summary.full_mgmt_fees) });
            case 'billableMgmtFees':
                return _jsx(Typography, { sx: { fontSize: '14px !important' }, children: formatCurrency(summary.billable_mgmt_fees) });
            case 'commissionRate':
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: summary.type?.map((t) => {
                        const rate = t === 'paid_media' ? summary.paid_comm_rate : summary.aff_comm_rate;
                        return (_jsx(Typography, { sx: { fontSize: '14px !important', py: '2px' }, children: rate !== null && rate !== undefined ? `${rate}%` : '-' }, t));
                    }) }));
            case 'measurement':
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: summary.type?.map((t) => {
                        const channels = (t === 'paid_media'
                            ? summary.paid_measurement_channel
                            : summary.aff_measurement_channel) || [];
                        return _jsx(Box, { children: renderMeasurementChannels(channels) }, t);
                    }) }));
            case 'prevYearRev':
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: summary.type?.map((t) => {
                        const val = t === 'paid_media' ? summary.paid_prev_year_revenue : summary.aff_prev_year_revenue;
                        return (_jsx(Typography, { sx: { fontSize: '14px !important', py: '2px' }, children: formatCurrency(val) }, t));
                    }) }));
            case 'growthRate':
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: summary.type?.map((t) => {
                        const v = t === 'paid_media' ? summary.paid_yoy_growth_rate : summary.aff_yoy_growth_rate;
                        const isPos = v >= 0;
                        return (_jsx(Box, { sx: { display: 'flex', alignItems: 'center', py: '2px' }, children: _jsxs(Box, { sx: {
                                    backgroundColor: isPos ? "#ecfdf3" : "#fef3f2",
                                    borderRadius: "16px",
                                    px: 1,
                                    py: 0.2,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5
                                }, children: [_jsxs(Typography, { sx: {
                                            fontFamily: "Inter",
                                            fontWeight: 500,
                                            fontSize: "12px",
                                            color: isPos ? "#027a48" : "#b42318"
                                        }, children: [Math.abs(v).toLocaleString(), "%"] }), _jsx("img", { src: isPos ? "/assets/trend-up.svg" : "/assets/trending-down.svg", alt: "", style: { width: 12, height: 12 } })] }) }, t));
                    }) }));
            case 'revenue':
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: summary.type?.map((t) => {
                        const val = t === 'paid_media' ? summary.paid_revenue : summary.aff_revenue;
                        return (_jsx(Typography, { sx: { fontSize: '14px !important', py: '2px' }, children: formatCurrency(val) }, t));
                    }) }));
            case 'roas':
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: summary.type?.map((t) => {
                        const v = t === 'paid_media' ? summary.paid_roas : summary.aff_roas;
                        return (_jsx(Typography, { sx: { fontSize: '14px !important', py: '2px' }, children: v ? Number(v).toFixed(2) : '-' }, t));
                    }) }));
            case 'clockifyGoal':
                return (_jsx(Typography, { sx: { fontSize: '14px !important' }, children: summary.clockify_goal ? `${summary.clockify_goal} hrs` : '-' }));
            case 'prev_month_revenue':
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: summary.type?.map((t) => {
                        const val = t === 'paid_media' ? summary.paid_prev_month_revenue : summary.aff_prev_month_revenue;
                        return (_jsx(Typography, { sx: { fontSize: '14px !important', py: '2px' }, children: formatCurrency(val) }, t));
                    }) }));
            case 'prev_month_projection':
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: summary.type?.map((t) => {
                        const val = t === 'paid_media' ? summary.paid_prev_month_projection : summary.aff_prev_month_projection;
                        return (_jsx(Typography, { sx: { fontSize: '14px !important', py: '2px' }, children: formatCurrency(val) }, t));
                    }) }));
            case 'current_month_revenue':
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: summary.type?.map((t) => {
                        const val = t === 'paid_media' ? summary.paid_current_month_revenue : summary.aff_current_month_revenue;
                        return (_jsx(Typography, { sx: { fontSize: '14px !important', py: '2px' }, children: formatCurrency(val) }, t));
                    }) }));
            case 'approval_status':
                const formatStatus = (s) => {
                    if (!s || s === 'pending')
                        return 'Pending';
                    return s.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                };
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: summary.type?.map((t) => {
                        const status = t === 'paid_media' ? summary.paid_approval_status : summary.aff_approval_status;
                        return (_jsx(Box, { sx: { py: '2px' }, children: _jsx(CommonChip, { label: formatStatus(status), index: status === 'approved' ? 2 : 1, renderType: "tag", sx: { textTransform: 'none', fontSize: '14px', fontWeight: 500 } }) }, t));
                    }) }));
            case 'curr_plan':
            case 'prev1_plan':
            case 'prev2_plan': {
                const offsetMap = { curr_plan: 0, prev1_plan: -1, prev2_plan: -2 };
                const colOffset = offsetMap[column.id];
                const monthLabel = getMonthLabel(year_month, colOffset);
                const eligibleTypes = column.id === 'curr_plan'
                    ? (summary.type || [])
                    : (summary.type || []).filter((t) => t === 'affiliate');
                if (eligibleTypes.length === 0)
                    return _jsx(Typography, { sx: { fontSize: '14px !important', color: '#667085' }, children: "-" });
                const getPlanCount = (t) => {
                    if (t === 'paid_media') {
                        if (column.id === 'curr_plan')
                            return summary.paid_curr_plan_count;
                        if (column.id === 'prev1_plan')
                            return summary.paid_prev1_plan_count;
                        return summary.paid_prev2_plan_count;
                    }
                    if (column.id === 'curr_plan')
                        return summary.aff_curr_plan_count;
                    if (column.id === 'prev1_plan')
                        return summary.aff_prev1_plan_count;
                    return summary.aff_prev2_plan_count;
                };
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.75 }, children: eligibleTypes.map((t) => {
                        const count = getPlanCount(t);
                        return (_jsxs(Box, { onClick: (e) => {
                                e.stopPropagation();
                                onActionClick?.({
                                    client: { id: summary.clientId || summary.client_id, name: summary.clientName, status: summary.status },
                                    action_type: 'plan',
                                    service_type: t,
                                    month_label: monthLabel,
                                    campaign_id: summary.campaign_id,
                                    connection_id: summary.connection_id
                                });
                            }, sx: {
                                display: 'flex', alignItems: 'center', gap: 0.75,
                                bgcolor: '#F4F3FF', border: '1px solid #D9D6FE', borderRadius: '8px',
                                px: 1.25, py: '4px', cursor: 'pointer', width: 'fit-content',
                                '&:hover': { bgcolor: '#ede9fe' }, transition: 'background 0.15s'
                            }, children: [_jsx(Box, { component: "img", src: "/assets/plan.svg", alt: "plan", sx: { width: 15, height: 15 } }), _jsxs(Typography, { sx: { fontSize: '12px', fontWeight: 500, color: '#5B21B6', whiteSpace: 'nowrap' }, children: ["Add Plan", count ? ` (${count})` : ''] })] }, t));
                    }) }));
            }
            case 'note_action': {
                return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: summary.type?.map((t) => {
                        const count = t === 'paid_media' ? summary.paid_note_count : summary.aff_note_count;
                        return (_jsx(Box, { sx: { py: '2px' }, children: _jsxs(Box, { onClick: (e) => {
                                    e.stopPropagation();
                                    onActionClick?.({
                                        client: { id: summary.clientId || summary.client_id, name: summary.clientName, status: summary.status },
                                        action_type: 'note',
                                        service_type: t,
                                        month_label: 'Monthly Progress',
                                        campaign_id: summary.campaign_id,
                                        connection_id: summary.connection_id
                                    });
                                }, sx: {
                                    display: 'flex', alignItems: 'center', gap: 0.75,
                                    bgcolor: '#FFFAEB', border: '1px solid #FEDF89', borderRadius: '8px',
                                    px: 1.25, py: '4px', cursor: 'pointer', whiteSpace: 'nowrap', width: 'fit-content',
                                    '&:hover': { bgcolor: '#fef3c7' }, transition: 'background 0.15s'
                                }, children: [_jsx(Box, { component: "img", src: "/assets/note.svg", alt: "note", sx: { width: 15, height: 15 } }), _jsxs(Typography, { sx: { fontSize: '12px', fontWeight: 500, color: '#B45309' }, children: ["Add Notes", count ? ` (${count})` : ''] })] }) }, t));
                    }) }));
            }
            default:
                return row[column.id] || '-';
        }
    };
    return { columns, renderCell };
};
